/**
 * Cards that tilt with the phone (the PDApps window's collection).
 *
 * ONE motion listener for the whole page: it writes two numbers (`--tilt-x`, `--tilt-y`,
 * each −1…1) on the page's root once per screen frame, and every card's CSS reads them.
 * Touch devices only — some laptops send motion readings too, and a mouse already tilts
 * the card under it. The readings never leave the device: nothing is sent, logged or
 * stored, only drawn.
 *
 * Runs only while the window is on screen and the browser tab is visible. "Level" is
 * however the phone is held when the page opens, and it slowly follows the phone, so
 * lying down works too. A phone that can't keep up turns it off by itself.
 *
 * iPhones ask permission for motion, and only on a tap: the Tilt switch asks; once
 * granted, the choice is remembered and re-asked silently on the first tap of a later
 * visit (iOS answers without a prompt once allowed).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { readPref, writePref } from './collection'

const TILT_PREF = 'papan.pdapps.tilt'
/** How far (degrees of phone movement) gives the full tilt. */
const FULL_TILT_DEG = 22
/** How fast "level" follows the phone, per reading (~4 s to settle at 60 Hz). */
const LEVEL_FOLLOW = 0.004
/**
 * A phone averaging slower than this per frame over the first second stops tilting
 * (under ~27 frames a second it looks broken). 30 Hz — an iPhone in Low Power Mode, a
 * phone saving battery — tilts fine, so it stays on.
 */
const SLOW_FRAME_MS = 37

type PermissionApi = { requestPermission?: () => Promise<'granted' | 'denied'> }

const isTouchDevice = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(hover: none) and (pointer: coarse)').matches
const reducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
const isAppleTouch = () =>
  typeof navigator !== 'undefined'
  && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
const orientationApi = (): PermissionApi | null =>
  typeof window !== 'undefined' && 'DeviceOrientationEvent' in window
    ? (window.DeviceOrientationEvent as unknown as PermissionApi)
    : null

export interface GyroTilt {
  /** A touch device with motion readings (and no reduced-motion ask): show the switch. */
  supported: boolean
  /** Tilting now. */
  on: boolean
  /** The Tilt switch. Asks the iPhone's permission when it needs it (call from a tap). */
  toggle: () => void
}

export function useGyroTilt(root: HTMLElement | null, active: boolean): GyroTilt {
  const api = orientationApi()
  const supported = !!api && isTouchDevice() && !reducedMotion()
  // Only iOS / iPadOS gate motion behind a tap. Newer Chrome also has `requestPermission`
  // (it always grants, and sends readings without it), so it doesn't count there.
  const needsPermission = supported && isAppleTouch() && typeof api?.requestPermission === 'function'
  const [pref, setPref] = useState<'on' | 'off' | null>(() => readPref(TILT_PREF, ['on', 'off'] as const))
  // An iPhone starts without permission (asked again on the first tap, below); others need none.
  const [granted, setGranted] = useState(!needsPermission)
  const [tooSlow, setTooSlow] = useState(false)
  // Android: on unless switched off. iPhone: only once the person has switched it on.
  const wanted = pref === 'on' || (pref === null && !needsPermission)
  const on = supported && wanted && granted && !tooSlow

  /** `true` granted, `false` refused, `null` not asked (no tap to ask on — try the next one). */
  const ask = useCallback(async (): Promise<boolean | null> => {
    try {
      const answer = await api?.requestPermission?.()
      const ok = answer === 'granted'
      setGranted(ok)
      return ok
    } catch { return null }
  }, [api])

  const toggle = useCallback(() => {
    // Off is what the switch shows when `on` is false — tilt stopped for a slow phone included.
    if (on) { setPref('off'); writePref(TILT_PREF, 'off'); return }
    const turnOn = () => { setTooSlow(false); setPref('on'); writePref(TILT_PREF, 'on') }
    if (needsPermission && !granted) void ask().then((ok) => { if (ok) turnOn() })
    else turnOn()
  }, [on, granted, needsPermission, ask])

  // iPhone, switched on in an earlier visit: re-ask on a tap (silent once allowed). iOS
  // counts a CLICK as the tap (not a pointerdown); an ask it didn't take waits for the
  // next tap, a refusal stops asking.
  useEffect(() => {
    if (!root || !needsPermission || granted || pref !== 'on') return
    let done = false
    const onTap = () => { void ask().then((ok) => { if (ok !== null) { done = true; root.removeEventListener('click', onTap) } }) }
    root.addEventListener('click', onTap)
    return () => { if (!done) root.removeEventListener('click', onTap) }
  }, [root, needsPermission, granted, pref, ask])

  // The listener — only while tilting, the window is shown and the tab visible.
  const [tabVisible, setTabVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible')
  useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])
  const frames = useRef<number[]>([])
  useEffect(() => {
    if (!root || !on || !active || !tabVisible) return
    let level: { x: number; y: number } | null = null
    let next = { x: 0, y: 0 }
    let raf = 0
    frames.current = []
    // Over the first 60 screen frames (whether readings arrive or not — a phone lying still
    // sends few), measure; a phone that can't keep up stops tilting.
    let measure = requestAnimationFrame(function tick(now) {
      const f = frames.current
      f.push(now)
      if (f.length < 61) { measure = requestAnimationFrame(tick); return }
      measure = 0
      if ((f[60] - f[0]) / 60 > SLOW_FRAME_MS) setTooSlow(true)
    })
    const write = () => {
      raf = 0
      root.style.setProperty('--tilt-x', next.x.toFixed(3))
      root.style.setProperty('--tilt-y', next.y.toFixed(3))
    }
    const onReading = (e: DeviceOrientationEvent) => {
      if (e.beta == null || e.gamma == null) return
      // The phone's axes, turned to the screen's (a sideways phone swaps them).
      const angle = (screen.orientation?.angle ?? 0) % 360
      const b = e.beta
      const g = e.gamma
      const [x, y] = angle === 90 ? [b, -g] : angle === 270 || angle === -90 ? [-b, g] : angle === 180 ? [-g, -b] : [g, b]
      if (!level) level = { x, y }
      level.x += (x - level.x) * LEVEL_FOLLOW
      level.y += (y - level.y) * LEVEL_FOLLOW
      const clamp = (v: number) => Math.max(-1, Math.min(1, v / FULL_TILT_DEG))
      next = { x: clamp(x - level.x), y: clamp(y - level.y) }
      if (!raf) raf = requestAnimationFrame(write)
    }
    window.addEventListener('deviceorientation', onReading)
    return () => {
      window.removeEventListener('deviceorientation', onReading)
      if (raf) cancelAnimationFrame(raf)
      if (measure) cancelAnimationFrame(measure)
      root.style.removeProperty('--tilt-x')
      root.style.removeProperty('--tilt-y')
    }
  }, [root, on, active, tabVisible])

  return { supported, on, toggle }
}
